import { useState } from "react";
import { useSelector } from "react-redux";
import { openPaymentPage } from "api/BillingAPI";
import { getUserId, getUserEmail, getPaymentAmount } from "selectors/userInfo";
import {
	Overlay,
	ModalCard,
	CloseButton,
	IconWrapper,
	Title,
	Description,
	PayButton,
	Price,
	CheckboxGroup,
	CheckboxWrapper,
	Checkbox,
	CheckboxText,
} from "./styledComponents";

const OFFER_URL = "https://pddtv.ru/oferta";
const PRIVACY_URL = "https://pddtv.ru/privacy";

/** Иконка блокировки */
const LockIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
		<rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
		<path d="M7 11V7a5 5 0 0 1 10 0v4" />
	</svg>
);

/** Иконка закрытия */
const CloseIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
		<path d="M18 6L6 18M6 6l12 12" />
	</svg>
);

type PaymentModalProps = {
	/** Вариант отображения: block - блокирующий, confirm - подтверждение */
	variant: "block" | "confirm";
	/** Заголовок модального окна */
	title: string;
	/** Описание */
	description: string;
	/** Callback при закрытии (только для variant="confirm") */
	onClose?: () => void;
};

/**
 * Универсальный компонент модального окна оплаты
 */
const PaymentModal = ({
	variant,
	title,
	description,
	onClose,
}: PaymentModalProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const [paymentError, setPaymentError] = useState("");
	const [acceptedOffer, setAcceptedOffer] = useState(false);
	const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
	const userId = useSelector(getUserId);
	const userEmail = useSelector(getUserEmail);
	const paymentAmount = useSelector(getPaymentAmount);

	const canPay = acceptedOffer && acceptedPrivacy;

	const handlePayment = async () => {
		setIsLoading(true);
		setPaymentError("");
		try {
			await openPaymentPage("web");
		} catch (error) {
			const userIdentifier = userEmail || `ID: ${userId}`;
			setPaymentError(
				`${String(error)}. Пользователь: ${userIdentifier}`
			);
			console.error("Ошибка при создании платежа:", error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleOverlayClick = (e: React.MouseEvent) => {
		if (variant === "confirm" && e.target === e.currentTarget && onClose) {
			onClose();
		}
	};

	return (
		<Overlay onClick={handleOverlayClick}>
			<ModalCard>
				{variant === "confirm" && onClose && (
					<CloseButton onClick={onClose} aria-label="Закрыть">
						<CloseIcon />
					</CloseButton>
				)}
				{variant === "block" && (
					<IconWrapper>
						<LockIcon />
					</IconWrapper>
				)}
				<Title>{title}</Title>
				<Description>{description}</Description>
				<Price>
					{paymentAmount.toLocaleString("ru-RU")} ₽{" "}
					<span>/ разовый платёж</span>
				</Price>
				{paymentError && (
				<p style={{ color: "red", fontSize: 14, margin: "0 0 8px" }}>
					{paymentError}
				</p>
			)}
			<PayButton onClick={handlePayment} disabled={isLoading || !canPay}>
					{isLoading ? "Загрузка..." : "Оплатить доступ"}
				</PayButton>
				<CheckboxGroup>
					<CheckboxWrapper>
						<Checkbox
							id={`offer-checkbox-${variant}`}
							checked={acceptedOffer}
							onChange={(e) => setAcceptedOffer(e.target.checked)}
						/>
						<CheckboxText>
							Я принимаю условия{" "}
							<a href={OFFER_URL} target="_blank" rel="noopener noreferrer">
								Договора-оферты
							</a>
						</CheckboxText>
					</CheckboxWrapper>
					<CheckboxWrapper>
						<Checkbox
							id={`privacy-checkbox-${variant}`}
							checked={acceptedPrivacy}
							onChange={(e) => setAcceptedPrivacy(e.target.checked)}
						/>
						<CheckboxText>
							Я соглашаюсь с{" "}
							<a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
								Политикой обработки персональных данных
							</a>
						</CheckboxText>
					</CheckboxWrapper>
				</CheckboxGroup>
			</ModalCard>
		</Overlay>
	);
};

export default PaymentModal;

